import { ApiError, apiFetch } from '@/lib/apiFetch';
import { playCoins, prefetchCoins } from '@/lib/sounds';
import { useCallback, useEffect, useRef, useState } from 'react';

type Zakazka = {
  id: string;
  order_ref: string;
  product_code: string;
  product_name: string;
  total_qty: number;
  image_url: string | null;
  stav: string;
  svadlenka: string;
  svadlenka_color: string;
  in_progress: boolean;
  finished: boolean;
};

type Svadlenka = { id: string; name: string; color: string; code: string };

/** Krok průvodce — podle toho, co se zrovna skenuje. */
type Step = 'zakazka' | 'svadlenka' | 'start' | 'konec';

const HINT: Record<Step, string> = {
  zakazka: 'Naskenujte zakázku',
  svadlenka: 'Naskenujte jméno švadlenky',
  start: 'Naskenujte START',
  konec: 'Naskenujte akci KONEC',
};

/** Po dokončení se hláška chvíli ukáže a pak se dílna vrátí na začátek. */
const RESET_MS = 5000;

/**
 * Šicí dílna — obrazovka ovládaná výhradně čtečkou. Švadlenka naskenuje
 * zakázku (čárový kód z A6 lístku), svůj kód a akci START; u rozdělané
 * zakázky rovnou akci KONEC. Zápis jde do zrcadla zakázek (stav, časy šití).
 */
export function SiciDilnaPage() {
  const [step, setStep] = useState<Step>('zakazka');
  const [value, setValue] = useState('');
  const [zakazka, setZakazka] = useState<Zakazka | null>(null);
  const [svadlenka, setSvadlenka] = useState<Svadlenka | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Zvuk stáhneme dopředu, ať v okamžiku ukončení zakázky nic nedrhne.
  useEffect(() => {
    prefetchCoins();
  }, []);

  const reset = useCallback(() => {
    setStep('zakazka');
    setValue('');
    setZakazka(null);
    setSvadlenka(null);
    setErr(null);
    setDone(null);
    inputRef.current?.focus();
  }, []);

  // Kurzor patří vždycky do pole — čtečka „píše" do zaostřeného inputu.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Po úspěšné akci se obrazovka sama vrátí na začátek (další švadlenka).
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(reset, RESET_MS);
    return () => clearTimeout(t);
  }, [done, reset]);

  /** Špatná švadlenka — zpátky na její naskenování, zakázka zůstává načtená. */
  function changeSvadlenka() {
    setSvadlenka(null);
    setStep('svadlenka');
    setValue('');
    setErr(null);
    inputRef.current?.focus();
  }

  async function submit() {
    const scanned = value.trim();
    if (!scanned || busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (step === 'zakazka') {
        const z = await apiFetch<Zakazka>(
          `/api/vyroba/dilna/zakazka/${encodeURIComponent(scanned)}`,
        );
        if (z.finished) {
          setErr(`Zakázka ${z.order_ref} je už ve stavu „${z.stav}".`);
        } else {
          setZakazka(z);
          setStep(z.in_progress ? 'konec' : 'svadlenka');
        }
      } else if (step === 'svadlenka') {
        const s = await apiFetch<Omit<Svadlenka, 'code'>>(
          `/api/vyroba/dilna/svadlenka/${encodeURIComponent(scanned)}`,
        );
        setSvadlenka({ ...s, code: scanned });
        setStep('start');
      } else if (step === 'start') {
        if (scanned.toUpperCase() !== 'START') {
          setErr('Naskenujte prosím kód START.');
        } else if (zakazka && svadlenka) {
          await apiFetch('/api/vyroba/dilna/start', {
            method: 'POST',
            body: { order_ref: Number(zakazka.order_ref), svadlenka_code: svadlenka.code },
          });
          setDone('Jde se na to!');
        }
      } else if (step === 'konec') {
        if (scanned.toUpperCase() !== 'KONEC') {
          setErr('Naskenujte prosím kód KONEC.');
        } else if (zakazka) {
          await apiFetch('/api/vyroba/dilna/konec', {
            method: 'POST',
            body: { order_ref: Number(zakazka.order_ref) },
          });
          setDone('Zakázka úspěšně ukončena.');
          // Odměna za hotovou zakázku — po třech vteřinách cinknou penízky.
          playCoins(3);
        }
      }
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Něco se nepovedlo, zkuste to znovu.');
    } finally {
      setValue('');
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  const steps = step === 'konec' || (zakazka?.in_progress ?? false) ? 2 : 3;
  const current = step === 'zakazka' ? 1 : step === 'svadlenka' || step === 'konec' ? 2 : 3;

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-3xl bg-surface rounded-2xl shadow-card px-6 py-8">
        <h1 className="text-3xl font-bold text-text mb-8">Výroba — švadlenky</h1>

        <Stepper count={steps} current={current} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="mt-10 flex flex-col items-center"
        >
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            // Čtečka píše rychle a zakončí Enterem — pole je pořád zaostřené.
            className="w-full max-w-md border-0 border-b-2 border-text bg-transparent text-center text-2xl font-bold text-text focus:outline-none focus:border-primary py-2"
          />
          <span className="mt-2 text-lg italic text-text-secondary">{HINT[step]}</span>
        </form>

        {err && <p className="mt-6 text-center text-base font-semibold text-danger">{err}</p>}

        {(svadlenka || (zakazka?.in_progress && zakazka.svadlenka)) && (
          <div className="mt-8 flex justify-center">
            <div className="relative">
              <div
                className="rounded-lg px-10 py-8 text-2xl font-bold text-white text-center"
                style={{
                  backgroundColor: svadlenka?.color || zakazka?.svadlenka_color || '#c9d92e',
                }}
              >
                {svadlenka ? svadlenka.name : `Zakázku právě šije ${zakazka?.svadlenka}`}
              </div>
              {svadlenka && (
                // Špatně naskenovaná švadlenka — křížek vrátí krok 2, zakázka zůstane.
                <button
                  type="button"
                  onClick={changeSvadlenka}
                  aria-label="Změnit švadlenku"
                  title="Změnit švadlenku"
                  className="absolute -right-3 -top-3 h-9 w-9 rounded-full bg-danger text-white text-lg font-bold shadow hover:opacity-90 cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}

        {zakazka && (
          <div className="mt-8 relative">
            <button
              type="button"
              onClick={reset}
              aria-label="Začít znovu"
              className="absolute right-0 top-0 h-10 w-10 rounded-md bg-bg border border-border text-text-secondary text-lg hover:bg-surface-hover cursor-pointer"
            >
              ✕
            </button>
            <div className="flex flex-wrap justify-center gap-x-10 gap-y-2 text-lg">
              <span>
                <strong className="font-bold text-text">ID zakázky:</strong>{' '}
                <span className="text-text-secondary">{zakazka.order_ref}</span>
              </span>
              <span>
                <strong className="font-bold text-text">Kód produktu:</strong>{' '}
                <span className="text-text-secondary">{zakazka.product_code}</span>
              </span>
              <span>
                <strong className="font-bold text-text">Celkem ks:</strong>{' '}
                <span className="text-text-secondary">{zakazka.total_qty}</span>
              </span>
            </div>
            {zakazka.image_url && (
              <div className="mt-6 flex justify-center">
                <img
                  src={zakazka.image_url}
                  alt={zakazka.product_name}
                  className="max-h-80 object-contain"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {done && <DoneModal message={done} onClose={reset} />}
    </div>
  );
}

/** Kroky 1–3 (u rozdělané zakázky jen 1–2) s propojovací čárou. */
function Stepper({ count, current }: { count: number; current: number }) {
  const items = Array.from({ length: count }, (_, i) => i + 1);
  return (
    <div className="flex items-start justify-center">
      {items.map((n) => (
        <div key={n} className="flex items-start">
          {n > 1 && (
            <div
              className={`h-0.5 w-24 sm:w-40 mt-5 ${n <= current ? 'bg-action' : 'bg-border'}`}
            />
          )}
          <div className="flex flex-col items-center w-24">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-full text-base font-bold ${
                n <= current
                  ? 'bg-action text-white'
                  : 'border-2 border-border bg-surface text-text-muted'
              }`}
            >
              {n}
            </span>
            <span
              className={`mt-2 text-base ${
                n <= current ? 'font-bold text-action' : 'text-text-secondary'
              }`}
            >
              Krok {n}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Potvrzení akce — zmizí samo, dílna se pak vrátí na krok 1. */
function DoneModal({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 cursor-default"
      />
      <div className="relative bg-surface rounded-xl shadow-modal px-16 py-10 text-center reveal">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="72"
          height="72"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="mx-auto text-action"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M8 12.5l2.5 2.5L16 9.5" />
        </svg>
        <p className="mt-5 text-2xl font-bold text-text">{message}</p>
      </div>
    </div>
  );
}
